import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, Box, Typography, Chip, Avatar, Button, CircularProgress, alpha, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { fetchAssignedLeadsPaged, fetchUnassignedLeadsPaged, BackendLead } from '../utils/api';
import { useWhiteLabelHiding } from '../hooks/useWhiteLabelHiding';
import { useTranslation } from 'react-i18next';

interface WelcomeAssistantModalProps {
  open: boolean;
  onClose: () => void;
}

const WelcomeAssistantModal: React.FC<WelcomeAssistantModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [assignedLeads, setAssignedLeads] = useState<BackendLead[]>([]);
  const [unassignedLeads, setUnassignedLeads] = useState<BackendLead[]>([]);
  const { shouldHidePaymentElements } = useWhiteLabelHiding();

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return t('modals.welcome.goodMorning');
    if (hour < 18) return t('modals.welcome.goodAfternoon');
    return t('modals.welcome.goodEvening');
  }, [t]);

  useEffect(() => {
    if (!open) return;
    let isMounted = true;
    const load = async () => {
      try {
        const [assigned, unassigned] = await Promise.all([
          fetchAssignedLeadsPaged({ skip: 0, take: 100 }),
          fetchUnassignedLeadsPaged({ skip: 0, take: 100 })
        ]);
        if (!isMounted) return;

        // Debug logs
        console.log('Assigned leads response:', assigned, 'Type:', typeof assigned, 'IsArray:', Array.isArray(assigned));
        console.log('Unassigned leads response:', unassigned, 'Type:', typeof unassigned, 'IsArray:', Array.isArray(unassigned));

        // Garantir que sempre sejam arrays
        setAssignedLeads(Array.isArray(assigned) ? assigned : []);
        setUnassignedLeads(Array.isArray(unassigned) ? unassigned : []);
      } catch (error) {
        console.error('Error loading leads in WelcomeAssistantModal:', error);
        // Garantir que sempre sejam arrays mesmo em caso de erro
        setAssignedLeads([]);
        setUnassignedLeads([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    setLoading(true);
    load();
    return () => { isMounted = false; };
  }, [open]);

  function normalizeStatus(raw: string | undefined | null): 'novo' | 'contato_inicial' | 'qualificado' | 'proposta' | 'negociacao' | 'fechado' | 'perdido' | 'outro' {
    const s = String(raw || '').toLowerCase().replace(/\s+/g, '_');
    if (s === 'novo') return 'novo';
    if (s === 'contato_inicial' || s === 'contato-inicial' || s === 'contatoinicial' || s === 'emandamento' || s === 'em_andamento') return 'contato_inicial';
    if (s === 'qualificado') return 'qualificado';
    if (s === 'proposta') return 'proposta';
    if (s === 'negociacao' || s === 'negociação') return 'negociacao';
    if (s === 'fechado' || s === 'finalizado') return 'fechado';
    if (s === 'perdido') return 'perdido';
    return 'outro';
  }

  const stats = useMemo(() => {
    // Garantir que sempre sejam arrays
    const assigned = Array.isArray(assignedLeads) ? assignedLeads : [];
    const unassigned = Array.isArray(unassignedLeads) ? unassignedLeads : [];

    const mine = assigned.filter(l => !l.assignedTo || (user && l.assignedTo === user.id));
    const totalAssigned = assigned.length;
    const myAssigned = mine.length;
    const responded = assigned.filter(l => normalizeStatus(l.status) !== 'novo').length;
    const novos = assigned.filter(l => normalizeStatus(l.status) === 'novo').length;
    const negociacao = assigned.filter(l => {
      const st = normalizeStatus(l.status);
      return st === 'negociacao' || st === 'proposta';
    }).length;
    const fechado = assigned.filter(l => normalizeStatus(l.status) === 'fechado').length;
    const perdido = assigned.filter(l => normalizeStatus(l.status) === 'perdido').length;
    const newToday = assigned.filter(l => {
      const created = l.creationTime ? new Date(l.creationTime) : null;
      if (!created) return false;
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return created >= start;
    }).length;
    const available = unassigned.filter(l => !l.assignedTo).length;
    return { totalAssigned, myAssigned, responded, novos, negociacao, fechado, perdido, newToday, available };
  }, [assignedLeads, unassignedLeads, user]);

  const buildSuggestions = (): string[] => {
    const suggestions: string[] = [];
    if (stats.available > 0) suggestions.push(t('modals.welcome.suggestion1', { count: stats.available }));
    if (stats.novos > 0) suggestions.push(t('modals.welcome.suggestion2', { count: stats.novos }));
    if (stats.negociacao > 0) suggestions.push(t('modals.welcome.suggestion3', { count: stats.negociacao }));
    if (stats.responded < stats.totalAssigned) suggestions.push(t('modals.welcome.suggestion4', { count: stats.totalAssigned - stats.responded }));
    if (stats.newToday > 0) suggestions.push(t('modals.welcome.suggestion5', { count: stats.newToday }));
    if (stats.perdido > 0 && stats.negociacao === 0 && stats.novos === 0) suggestions.push(t('modals.welcome.suggestion6', { count: stats.perdido }));
    if (suggestions.length === 0) suggestions.push(t('modals.welcome.suggestion7'));
    return suggestions;
  };

  const [typedText, setTypedText] = useState('');
  const fullMessage = useMemo(() => {
    const name = user?.name?.split(' ')[0] || t('modals.welcome.you');
    return t('modals.welcome.fullMessage', { 
      greeting, 
      name, 
      novos: stats.novos, 
      negociacao: stats.negociacao, 
      fechado: stats.fechado, 
      perdido: stats.perdido, 
      myAssigned: stats.myAssigned, 
      available: stats.available 
    });
  }, [greeting, user?.name, stats, t]);

  useEffect(() => {
    if (!open) return;
    setTypedText('');
    let i = 0;
    const interval = setInterval(() => {
      i++;
      setTypedText(fullMessage.slice(0, i));
      if (i >= fullMessage.length) clearInterval(interval);
    }, 18);
    return () => clearInterval(interval);
  }, [open, fullMessage]);

  // Se deve ocultar elementos de pagamento, não renderizar o modal
  if (shouldHidePaymentElements) {
    return null;
  }

  return (
    <Dialog open={open} onClose={() => { }} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
      <DialogContent sx={(theme) => ({
        p: { xs: 2, sm: 4 },
        background: theme.palette.mode === 'dark'
          ? `radial-gradient(circle at 20% 20%, ${alpha('#1a1a1a', 0.15)} 0%, transparent 40%), radial-gradient(circle at 80% 80%, ${alpha('#1a1a1a', 0.15)} 0%, transparent 40%), linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`
          : `radial-gradient(circle at 20% 20%, ${alpha('#1a1a1a', 0.08)} 0%, transparent 40%), radial-gradient(circle at 80% 80%, ${alpha('#1a1a1a', 0.08)} 0%, transparent 40%), linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)`
      })}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Avatar sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? '#65f0c5' : '#1a1a1a', width: 48, height: 48, color: theme.palette.mode === 'dark' ? theme.palette.getContrastText('#65f0c5') : theme.palette.getContrastText('#1a1a1a') })}>J</Avatar>
          <Typography variant="h6" sx={(theme) => ({ fontWeight: 800, background: theme.palette.mode === 'dark' ? 'linear-gradient(135deg, #93c5fd 0%, #c4b5fd 100%)' : 'linear-gradient(135deg, #1a1a1a 0%, #4b6a89 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' })}>
            {t('modals.welcome.title')}
          </Typography>
        </Box>

        <Box sx={{ minHeight: 64, mb: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body1">{t('modals.welcome.loadingSummary')}</Typography>
            </Box>
          ) : (
            <Typography variant="subtitle1" sx={(theme) => ({ lineHeight: 1.6 })}>
              {typedText}<span style={{ display: 'inline-block', width: 8, marginLeft: 2, background: theme.palette.mode === 'dark' ? '#93c5fd' : '#1a1a1a', height: 18, verticalAlign: 'baseline', animation: 'blink 1s step-end infinite' }} />
            </Typography>
          )}
        </Box>

        {!loading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('modals.welcome.funnelSummary')}</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
              <StatCard label={t('modals.welcome.new')} value={stats.novos} color="#4f46e5" />
              <StatCard label={t('modals.welcome.negotiation')} value={stats.negociacao} color="#ef4444" />
              <StatCard label={t('modals.welcome.closed')} value={stats.fechado} color="#22c55e" />
              <StatCard label={t('modals.welcome.lost')} value={stats.perdido} color="#9333ea" />
            </Box>
          </Box>
        )}

        {!loading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{t('modals.welcome.suggestions')}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {buildSuggestions().map((s, idx) => (
                <Chip key={idx} label={s} color="primary" variant="outlined" sx={(theme) => ({ borderRadius: 2, borderColor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.3)' : undefined })} />
              ))}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, color: theme.palette.mode === 'dark' ? '#000' : undefined  }}>
            {t('modals.welcome.start')}
          </Button>
        </Box>

        <style>{`
          @keyframes blink { 50% { opacity: 0; } }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

const StatCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <Paper elevation={1} sx={(theme) => ({ p: 1.5, borderRadius: 2, borderLeft: `4px solid ${color}`, background: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'white' })}>
    <Typography variant="h6" sx={{ fontWeight: 800, color, mb: 0.5 }}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Paper>
);

export default WelcomeAssistantModal;


